import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  MdAddShoppingCart,
  MdCheckCircle,
  MdClose,
} from "react-icons/md";
import { hideRootModal } from "../rootModalSlice";
import styled, { createGlobalStyle } from "styled-components/macro";
import Modal from "react-modal";
import { updateCartData } from "features/cart/cartSlice";
import { Product } from "features/ProductReel/types";
import { colors, zIndexes } from "theme/Theme";
import Counter from "components/Counter/Counter";
import { CartData } from "features/cart/utils";
import SelectInstallation from "features/productDetails/components/SelectInstallation";
import userStorage from "store/UserStorage";
import { ProductCartItem } from "features/cart/types";
import { PageWithFilterType } from "features/Filters/types";
import { useInstallation } from "hooks/useInstallation";
import { setMessage } from "features/message/messageSlice";
import { MessageType } from "features/message/types";
import DualPrice from "components/Price";
import climaIMG from "assets/images/clima_img.png";
import { resolveEuroPrice, roundMoney } from "features/utils";

interface Props {
  item: Product;
  pageType: PageWithFilterType;
}

const getProductImage = (product: any) => {
  if (product?.image?.image) {
    return product.image;
  }

  if (Array.isArray(product?.images)) {
    return product.images.find((image: any) => image?.image);
  }

  return product?.images?.image ? product.images : null;
};

if (typeof document !== "undefined") {
  const appRoot = document.getElementById("root");
  if (appRoot) {
    Modal.setAppElement(appRoot);
  }
}

const CheckoutModal: React.FC<Props> = ({ item, pageType }) => {
  const dispatch = useDispatch();
  const hasFreeMounting = Boolean((item.description as any)?.free_mounting);
  const { applyInstallation, installationPrice, installationPriceEur } =
    useInstallation(item.power_btu, item.type);

  const cartItems = userStorage.getStoredInCart();
  const [count, setCount] = useState(1);
  const [cartData, setCartData] = useState<CartData>({
    product: item,
    installation: hasFreeMounting,
  });

  const closeModal = () => {
    dispatch(hideRootModal());
  };

  const addProduct = () => {
    if (cartItems.length === 0) {
      dispatch(
        updateCartData([
          {
            product: cartData.product,
            installation: cartData.installation,
            count,
          },
        ])
      );
    } else {
      const newProductCardItems = [...cartItems];
      const element = cartItems.find(
        (cartItem: ProductCartItem) =>
          cartItem.product._id === cartData.product._id
      );

      if (element) {
        const newElement = { ...element };
        const newCount = newElement.count ? newElement.count + count : count;
        const index = cartItems.indexOf(element);

        newProductCardItems.splice(index, 1);
        newElement.count = newCount;
        dispatch(updateCartData([...newProductCardItems, newElement]));
      } else {
        dispatch(
          updateCartData([
            ...newProductCardItems,
            {
              product: cartData.product,
              installation: cartData.installation,
              count,
            },
          ])
        );
      }
    }

    dispatch(hideRootModal());
    dispatch(
      setMessage({
        text: "Продуктът беше успешно добавен в количката",
        type: MessageType.success,
      })
    );
  };

  const increase = () => {
    setCount((currentCount) => currentCount + 1);
  };

  const decrease = () => {
    setCount((currentCount) => Math.max(1, currentCount - 1));
  };

  const onInstallationChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const hasInstallation = event.target.value === "paid";
    const updatedProduct = applyInstallation(
      cartData.product,
      hasInstallation
    );

    setCartData({
      product: updatedProduct,
      installation: hasInstallation,
    });
  };

  const productImage = getProductImage(cartData.product);
  const hasPromotion = Number(cartData.product.price_promotion) > 0;
  const unitPriceBgn = Number(
    hasPromotion
      ? cartData.product.price_promotion
      : cartData.product.price
  );
  const unitPriceEur = resolveEuroPrice(
    unitPriceBgn,
    hasPromotion
      ? cartData.product.price_promotion_eur
      : cartData.product.price_eur
  );
  const totalPriceBgn = roundMoney(unitPriceBgn * count);
  const totalPriceEur = roundMoney(unitPriceEur * count);
  const isAccessory = pageType === PageWithFilterType.accessories;

  return (
    <>
      <CheckoutModalStyles />
      <Modal
        isOpen
        onRequestClose={closeModal}
        className="checkout-modal"
        overlayClassName="checkout-modal-overlay"
        contentLabel={`Добавяне на ${item.title} в количката`}
        shouldCloseOnEsc
        shouldCloseOnOverlayClick
      >
        <Header>
          <CloseButton
            type="button"
            onClick={closeModal}
            aria-label="Затвори прозореца"
          >
            <MdClose aria-hidden="true" />
          </CloseButton>
        </Header>

        <ModalBody>
          <ProductSummary>
            <ImageFrame>
              <ProductImage
                src={
                  productImage
                    ? `data:${productImage.contentType};base64,${productImage.image}`
                    : climaIMG
                }
                alt={productImage?.alt || item.title}
              />
            </ImageFrame>
            <ProductCopy>
              <ProductTitle>{item.title}</ProductTitle>
              <UnitPriceLabel>Единична цена</UnitPriceLabel>
              <UnitPrice>
                <DualPrice bgn={unitPriceBgn} eur={unitPriceEur} />
              </UnitPrice>
            </ProductCopy>
          </ProductSummary>

          <OptionsCard>
            <ControlRow>
              <ControlCopy>
                <ControlLabel>Количество</ControlLabel>
                <ControlHint>Изберете желания брой</ControlHint>
              </ControlCopy>
              <Counter
                itemCount={count}
                increase={increase}
                decrease={decrease}
                min={1}
              />
            </ControlRow>

            {isAccessory ? (
              <IncludedNote>
                <MdCheckCircle aria-hidden="true" />
                <span>Цената е с включено ДДС</span>
              </IncludedNote>
            ) : hasFreeMounting ? (
              <IncludedNote>
                <MdCheckCircle aria-hidden="true" />
                <span>Включени монтаж и ДДС</span>
              </IncludedNote>
            ) : (
              <InstallationField>
                <ControlLabel as="label" htmlFor="installation">
                  Монтаж
                </ControlLabel>
                <ControlHint>Добавете професионален монтаж по желание</ControlHint>
                <SelectInstallation
                  installationPrice={installationPrice}
                  installationPriceEur={installationPriceEur}
                  powerBtu={item.power_btu}
                  type={item.type}
                  onChange={onInstallationChange}
                />
              </InstallationField>
            )}
          </OptionsCard>

          <TotalRow aria-live="polite">
            <TotalCopy>
              <TotalLabel>Общо</TotalLabel>
              <TotalHint>{count} бр. с включено ДДС</TotalHint>
            </TotalCopy>
            <TotalPrice>
              <DualPrice bgn={totalPriceBgn} eur={totalPriceEur} />
            </TotalPrice>
          </TotalRow>
        </ModalBody>

        <ModalFooter>
          <SecondaryButton type="button" onClick={closeModal}>
            Продължи пазаруването
          </SecondaryButton>
          <PrimaryButton type="button" onClick={addProduct}>
            <MdAddShoppingCart aria-hidden="true" />
            Добави в количката
          </PrimaryButton>
        </ModalFooter>
      </Modal>
    </>
  );
};

const CheckoutModalStyles = createGlobalStyle`
  @keyframes checkout-overlay-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes checkout-dialog-in {
    from { opacity: 0; transform: translateY(12px) scale(0.985); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  .checkout-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: ${zIndexes.drawer + 1};
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 16px;
    overflow-y: auto;
    background: rgba(17, 24, 39, 0.62);
    backdrop-filter: blur(3px);
    animation: checkout-overlay-in 180ms ease-out;
  }

  .checkout-modal {
    position: relative;
    display: flex;
    width: min(100%, 560px);
    max-height: calc(100dvh - 32px);
    flex-direction: column;
    overflow: hidden;
    background: #ffffff;
    border: 1px solid #e5ebef;
    border-radius: 20px;
    outline: none;
    box-shadow: 0 24px 70px rgba(17, 24, 39, 0.24);
    animation: checkout-dialog-in 200ms ease-out;
  }

  @media (prefers-reduced-motion: reduce) {
    .checkout-modal-overlay,
    .checkout-modal {
      animation: none;
    }
  }

  @media (max-width: 767px) {
    .checkout-modal-overlay {
      align-items: flex-end;
      padding: 0;
    }

    .checkout-modal {
      width: 100%;
      max-height: calc(100dvh - 12px);
      border-right: 0;
      border-bottom: 0;
      border-left: 0;
      border-radius: 20px 20px 0 0;
      padding-bottom: env(safe-area-inset-bottom);
    }
  }
`;

const Header = styled.header`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  padding: 12px 16px 0;

  @media (max-width: 479px) {
    padding: 10px 12px 0;
  }
`;

const CloseButton = styled.button`
  display: inline-flex;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  align-items: center;
  justify-content: center;
  color: ${colors.dark};
  background: ${colors.newGray};
  border: 1px solid transparent;
  border-radius: 50%;
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    color: ${colors.darkest};
    background: ${colors.gray};
  }

  &:focus-visible {
    border-color: ${colors.light_blue};
    outline: 3px solid rgba(0, 152, 255, 0.18);
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  padding: 20px 24px;
  overflow-y: auto;
  overscroll-behavior: contain;

  @media (max-width: 479px) {
    gap: 14px;
    padding: 16px 18px;
  }
`;

const ProductSummary = styled.section`
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  gap: 16px;
  align-items: center;

  @media (max-width: 479px) {
    grid-template-columns: 88px minmax(0, 1fr);
    gap: 12px;
  }
`;

const ImageFrame = styled.div`
  display: flex;
  aspect-ratio: 4 / 3;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e8edf0;
  border-radius: 12px;
`;

const ProductImage = styled.img`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 8px;
  object-fit: contain;
`;

const ProductCopy = styled.div`
  min-width: 0;
`;

const ProductTitle = styled.h3`
  display: -webkit-box;
  margin: 0 0 10px;
  overflow: hidden;
  color: ${colors.darkest};
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`;

const UnitPriceLabel = styled.span`
  display: block;
  margin-bottom: 2px;
  color: ${colors.darkest_gray};
  font-size: 0.75rem;
  line-height: 1.3;
`;

const UnitPrice = styled.div`
  color: ${colors.darkest};
  font-size: 1.1rem;
  font-weight: 700;

  .price-eur {
    font-size: 1em;
  }
`;

const OptionsCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background: #f8fafb;
  border: 1px solid #e8edf0;
  border-radius: 14px;
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const ControlCopy = styled.div`
  min-width: 0;
`;

const ControlLabel = styled.span`
  display: block;
  color: ${colors.darkest};
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.35;
`;

const ControlHint = styled.span`
  display: block;
  margin-top: 3px;
  color: ${colors.darkest_gray};
  font-size: 0.75rem;
  line-height: 1.35;
`;

const InstallationField = styled.div`
  display: flex;
  flex-direction: column;

  select {
    width: 100%;
    min-height: 46px;
    margin-top: 10px;
    background: #ffffff;
    border-color: #cfd9df;
    border-radius: 9px;

    &:focus {
      border-color: ${colors.light_blue};
      outline: 3px solid rgba(0, 152, 255, 0.14);
    }
  }
`;

const IncludedNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 14px;
  color: #236449;
  border-top: 1px solid #e2e9e5;
  font-size: 0.8125rem;
  font-weight: 600;

  svg {
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
  }
`;

const TotalRow = styled.section`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding: 4px 2px 0;
`;

const TotalCopy = styled.div`
  min-width: 0;
`;

const TotalLabel = styled.span`
  display: block;
  color: ${colors.darkest};
  font-size: 1rem;
  font-weight: 700;
`;

const TotalHint = styled.span`
  display: block;
  margin-top: 2px;
  color: ${colors.darkest_gray};
  font-size: 0.75rem;
`;

const TotalPrice = styled.div`
  color: ${colors.darkest};
  font-size: 1.35rem;
  font-weight: 700;
  text-align: right;

  .price-eur {
    font-size: 1em;
  }

  .price-bgn {
    align-self: flex-end;
  }
`;

const ModalFooter = styled.footer`
  display: flex;
  flex: 0 0 auto;
  gap: 10px;
  padding: 16px 24px 20px;
  background: #ffffff;
  border-top: 1px solid #edf1f4;

  @media (max-width: 479px) {
    flex-direction: column;
    padding: 14px 18px 18px;
  }
`;

const FooterButton = styled.button`
  display: inline-flex;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 18px;
  border: 1px solid transparent;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid rgba(0, 152, 255, 0.2);
    outline-offset: 2px;
  }
`;

const SecondaryButton = styled(FooterButton)`
  flex: 0 1 auto;
  color: ${colors.blue};
  background: #ffffff;
  border-color: #b9ddf3;

  &:hover {
    background: ${colors.lightest_blue};
  }
`;

const PrimaryButton = styled(FooterButton)`
  flex: 1;
  color: #ffffff;
  background: ${colors.blue};
  border-color: ${colors.blue};

  svg {
    width: 21px;
    height: 21px;
  }

  &:hover {
    background: #006fbb;
    border-color: #006fbb;
  }
`;

export default CheckoutModal;
